import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    signOut as firebaseSignOut, 
    onAuthStateChanged, 
    User, 
    updateProfile, 
    GoogleAuthProvider, 
    signInWithPopup, 
    signInWithRedirect, 
    getRedirectResult, 
    sendPasswordResetEmail as firebaseSendPasswordReset 
} from 'firebase/auth';
import { auth, db } from './firebaseService';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { isPlatform } from '../utils/platform';

const googleProvider = new GoogleAuthProvider();

export interface UserProfile {
    uid: string;
    email: string;
    displayName: string;
    photoURL?: string;
    createdAt: Date;
    lastLoginAt: Date;
    onboardingComplete: boolean;
    subscriptionTier?: 'free' | 'premium' | 'pro';
    age?: number;
    gender?: string;
    location?: string;
    bio?: string;
    interests?: string[];
    culturalBackground?: string;
    preferences: {
        language: string;
        communicationStyle: 'formal' | 'casual';
    };
    symptomsData?: any;
    mentalHealthData?: any;
    consents?: any;
}

export const authService = {
    // Current user observer
    onAuthChange: (callback: (user: User | null) => void) => {
        return onAuthStateChanged(auth, callback);
    },

    // Sign up
    signUp: async (email: string, password: string, displayName: string): Promise<UserProfile> => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            await updateProfile(user, { displayName });

            const userProfile: UserProfile = {
                uid: user.uid,
                email: user.email!,
                displayName,
                createdAt: new Date(),
                lastLoginAt: new Date(),
                onboardingComplete: false,
                preferences: {
                    language: 'mixed',
                    communicationStyle: 'casual'
                }
            };

            await setDoc(doc(db, 'users', user.uid), {
                ...userProfile,
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp()
            });

            return userProfile;
        } catch (error: any) {
            console.error('Sign up error:', error);
            throw error;
        }
    },

    // Sign in
    signIn: async (email: string, password: string): Promise<UserProfile> => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const userProfile = await authService.getUserProfile(userCredential.user.uid);
            if (!userProfile) throw new Error('User profile not found');
            return userProfile;
        } catch (error: any) {
            console.error('Sign in error:', error);
            throw error;
        }
    },

    // Google Auth
    signInWithGoogle: async (): Promise<UserProfile> => {
        try {
            let user: User;
            if (isPlatform('hybrid')) {
                const result = await getRedirectResult(auth);
                if (result?.user) user = result.user;
                else {
                    await signInWithRedirect(auth, googleProvider);
                    throw new Error('Redirecting...');
                }
            } else {
                const result = await signInWithPopup(auth, googleProvider);
                user = result.user;
            }

            let profile = await authService.getUserProfile(user.uid);
            if (!profile) {
                profile = {
                    uid: user.uid,
                    email: user.email!,
                    displayName: user.displayName || 'User',
                    photoURL: user.photoURL || '',
                    createdAt: new Date(),
                    lastLoginAt: new Date(),
                    onboardingComplete: false,
                    preferences: {
                        language: 'mixed',
                        communicationStyle: 'casual'
                    }
                };
                await setDoc(doc(db, 'users', user.uid), {
                    ...profile,
                    createdAt: serverTimestamp(),
                    lastLoginAt: serverTimestamp()
                });
            }
            return profile;
        } catch (error: any) {
            console.error('Google Auth Error:', error);
            throw error;
        }
    },

    // Get profile
    getUserProfile: async (uid: string): Promise<UserProfile | null> => {
        const docSnap = await getDoc(doc(db, 'users', uid));
        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                lastLoginAt: data.lastLoginAt?.toDate() || new Date()
            } as UserProfile;
        }
        return null;
    },

    // Sign out
    signOut: async () => {
        return firebaseSignOut(auth);
    },

    // Password reset
    sendPasswordResetEmail: async (email: string) => {
        return firebaseSendPasswordReset(auth, email);
    }
};
